import { Suspense, useRef } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'dibs-react-intl';
import { graphql, createFragmentContainer } from 'react-relay/legacy';
import { messages } from '../messages/pdpDealerDetailsMessages';
import { trackSellerDetailsView } from '../tracking/pdpTracking';
import {
    MAKE_OFFER,
    CONTACT_SELLER,
} from '../PdpSharedContactTabsVariant/PdpSharedSellerTabsContants';

// components
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker';
import { PdpRespSellerItemTileCarousel } from '../PdpRespSimilarItems/PdpRespSellerItemTileCarousel/PdpRespSellerItemTileCarousel';
import { PdpSharedDealerDistinction } from '../PdpSharedDealerDistinctions/PdpSharedDealerDistinction';
import { PdpSharedCallDealerCta } from '../PdpSharedCallDealerCta/PdpSharedCallDealerCta';
import { ContactDealerInlineFormLoader } from 'dibs-buyer-layout/exports/ContactDealerInlineFormLoader';
import { PdpSharedSellerTabsVariantLazy } from '../PdpSharedContactTabsVariant/PdpSharedSellerTabsVariantLazy';
import { PdpSharedDealerDetailsBio } from '../PdpSharedDealerDetailsBio/PdpSharedDealerDetailsBio';
import { PdpSharedDealerDetailsHeader } from '../PdpSharedDealerDetailsHeader/PdpSharedDealerDetailsHeader';
import { PdpSharedDealerInfo } from '../PdpSharedDealerInfo/PdpSharedDealerInfo';

// styles
import styles from './PdpRespDealerDetails.scss';

const PdpRespDealerDetailsComponent = ({ item, sku, user, viewer, currency, isClient, dataTn }) => {
    const intl = useIntl();
    const { seller, dealerDetailsDisplayInfo, isSold: isSoldPdp } = item || {};
    const { isRewardsTier2, liveStorefrontProfile } = seller || {};
    const aboutUs = liveStorefrontProfile?.aboutUsUnbranded;
    const isNegotiable = dealerDetailsDisplayInfo?.ctas?.showMakeOffer || false;
    const shouldLoadEmbeddedMakeOffer = isClient && isNegotiable;
    const shouldLoadContactForm = isClient && !isNegotiable;
    const sellerDetailsRef = useRef(null);

    if (!item?.seller?.isAccessible) {
        return null;
    }

    const header =
        isRewardsTier2 && aboutUs ? (
            <>
                <div className={styles.detailsHeaderBio} data-tn="pdp-dealer-details-header-bio">
                    <div className={styles.leftSection}>
                        <PdpSharedDealerDetailsHeader
                            seller={seller}
                            user={user}
                            isClient={isClient}
                        />
                    </div>
                    <div className={`${styles.detailsBio} ${styles.rightSection}`}>
                        <PdpSharedDealerDetailsBio seller={seller} isClient={isClient} />
                    </div>
                </div>
                <div className={`${styles.detailsStatsContainer} ${styles.leftSection}`}>
                    <div className={styles.detailsSellerStats}>
                        <div className={styles.detailsSellerStatsColumn}>
                            <PdpSharedDealerDistinction seller={seller} />
                        </div>
                        <div className={styles.detailsSellerStatsColumn}>
                            <PdpSharedDealerInfo seller={seller} />
                        </div>
                    </div>
                </div>
            </>
        ) : (
            <div className={`${styles.detailsContainer} ${styles.leftSection}`}>
                <div className={styles.detailsHeader} data-tn="pdp-dealer-info">
                    <PdpSharedDealerDetailsHeader seller={seller} user={user} isClient={isClient} />
                </div>
                <div className={styles.detailsSellerStats}>
                    <div className={styles.detailsSellerStatsColumn}>
                        <PdpSharedDealerDistinction seller={seller} />
                    </div>
                    <div className={styles.detailsSellerStatsColumn}>
                        <PdpSharedDealerInfo seller={seller} />
                    </div>
                </div>
            </div>
        );

    return (
        <>
            <div className={styles.wrapper} ref={sellerDetailsRef} data-tn={dataTn}>
                <VisibilityTracker
                    elementRef={sellerDetailsRef}
                    onVisibilityChange={({ isVisible }) =>
                        trackSellerDetailsView(isNegotiable, isVisible)
                    }
                />
                {header}

                <div
                    className={`${styles.formContainer} ${styles.rightSection}`}
                    data-tn="pdp-contact-dealer-info"
                >
                    {shouldLoadEmbeddedMakeOffer ? (
                        <Suspense fallback={''}>
                            <PdpSharedSellerTabsVariantLazy
                                viewer={viewer}
                                item={item}
                                sku={sku}
                                user={user}
                                buttonProps={{ size: 'medium' }}
                                currency={currency}
                                tabOrder={
                                    user?.isVerifiedTrade
                                        ? [CONTACT_SELLER, MAKE_OFFER]
                                        : [MAKE_OFFER, CONTACT_SELLER]
                                }
                            />
                        </Suspense>
                    ) : (
                        <ContactDealerInlineFormLoader
                            viewer={viewer}
                            load={shouldLoadContactForm}
                            item={item}
                            user={user}
                            inputProps={{
                                hasAnimatedPlaceholder: true,
                                placeholder: intl.formatMessage(messages.namePlaceholder),
                            }}
                        />
                    )}
                    {shouldLoadContactForm && <PdpSharedCallDealerCta item={item} user={user} />}
                </div>
            </div>
            <div className={styles.carouselContainer}>
                {!isSoldPdp && (
                    <PdpRespSellerItemTileCarousel
                        user={user}
                        item={item}
                        viewer={viewer}
                        currency={currency}
                    />
                )}
            </div>
        </>
    );
};

PdpRespDealerDetailsComponent.propTypes = {
    user: PropTypes.object,
    relay: PropTypes.object,
    isClient: PropTypes.bool,
    currency: PropTypes.string,
    item: PropTypes.object.isRequired,
    sku: PropTypes.object,
    viewer: PropTypes.object.isRequired,
    dataTn: PropTypes.string,
};

export const PdpRespDealerDetails = createFragmentContainer(PdpRespDealerDetailsComponent, {
    viewer: graphql`
        fragment PdpRespDealerDetails_viewer on Viewer {
            ...ContactDealerInlineForm_viewer
            ...PdpSharedSellerTabsVariant_viewer

            ...PdpRespSellerItemTileCarousel_viewer
        }
    `,
    user: graphql`
        fragment PdpRespDealerDetails_user on User {
            isVerifiedTrade
            ...ContactDealerInlineForm_user
            ...PdpSharedSellerTabsVariant_user
            ...PdpSharedCallDealerCta_user
            ...PdpSharedDealerDetailsHeader_user

            ...PdpRespSellerItemTileCarousel_user
        }
    `,
    item: graphql`
        fragment PdpRespDealerDetails_item on Item {
            ... @include(if: $fetchPdp) {
                ...PdpSharedCallDealerCta_item
                ...ContactDealerInlineForm_item
                ...PdpSharedSellerTabsVariant_item
                dealerDetailsDisplayInfo: displayInfo {
                    ctas {
                        showMakeOffer
                    }
                }
                seller {
                    serviceId
                }
            }
            isSold
            seller {
                ...PdpSharedDealerDetailsBio_seller
                ...PdpSharedDealerDetailsHeader_seller
                ...PdpSharedDealerDistinction_seller
                ...PdpSharedDealerInfo_seller
                isRewardsTier2: isAnchorListMember(listName: "SELLER_REWARDS_2")
                liveStorefrontProfile {
                    aboutUsUnbranded
                }
                isAccessible
            }
            ...PdpRespSellerItemTileCarousel_item
        }
    `,
    sku: graphql`
        fragment PdpRespDealerDetails_sku on ItemSkuType {
            ...PdpSharedSellerTabsVariant_sku
        }
    `,
});
